import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MailAccount, MailBody, MailDiagnostics, MailMessage } from '@td/shared';
import { del, get, post } from '../lib/api';

const ACCOUNT = ['mail', 'account'];
const MESSAGES = ['mail', 'messages'];

export interface MailAccountState {
  account: MailAccount | null;
  /** False when the server has no MAIL_SECRET set. */
  configured: boolean;
}

export function useMailAccount() {
  return useQuery({
    queryKey: ACCOUNT,
    queryFn: () => get<MailAccountState>('/mail/account'),
  });
}

export function useConnectMail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; appPassword: string; displayName?: string }) =>
      post<MailAccount>('/mail/account', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ACCOUNT });
      void qc.invalidateQueries({ queryKey: MESSAGES });
    },
  });
}

export function useDisconnectMail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => del('/mail/account'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ACCOUNT });
      void qc.invalidateQueries({ queryKey: MESSAGES });
    },
  });
}

/**
 * Runs the connection test.
 *
 * A mutation rather than a query: it opens real sockets to Gmail and should
 * only ever happen because somebody pressed the button.
 */
export function useDiagnoseMail() {
  return useMutation({
    mutationFn: (input: { email?: string; appPassword?: string }) =>
      post<MailDiagnostics>('/mail/diagnose', input),
  });
}

export function useMessages(enabled: boolean, limit = 15) {
  return useQuery({
    queryKey: [...MESSAGES, limit],
    queryFn: () => get<MailMessage[]>(`/mail/messages?limit=${limit}`),
    enabled,
    /*
     * New post arrives through the live stream: the server holds an IMAP
     * connection open and says so the moment Gmail tells it. This interval is
     * the backstop for when that connection has quietly died — a mailbox that
     * is three minutes stale is a bad afternoon, not a disaster, and every
     * call here opens a connection to Gmail so it should not be a fast timer.
     */
    staleTime: 60 * 1000,
    refetchInterval: 3 * 60 * 1000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    retry: false,
  });
}

export function useMessageBody(uid: number | null) {
  return useQuery({
    queryKey: ['mail', 'body', uid],
    queryFn: () => get<MailBody>(`/mail/messages/${uid}`),
    enabled: uid !== null,
    // A message that has been read does not change.
    staleTime: 60 * 60 * 1000,
    retry: false,
  });
}

export type MailAction = 'read' | 'unread' | 'flag' | 'unflag' | 'archive' | 'trash';

export function useMessageAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ uid, action }: { uid: number; action: MailAction }) =>
      post(`/mail/messages/${uid}/action`, { action }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: MESSAGES }),
  });
}

export function useSendMail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      to: string;
      cc?: string;
      subject: string;
      text: string;
      inReplyTo?: string | null;
      references?: string | null;
      replyToUid?: number;
    }) => post<{ sent: boolean }>('/mail/send', input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: MESSAGES }),
  });
}
